/**
 * Native Shoes — Collection Infinite Scroll
 * -----------------------------------------------------------------
 * Author:   Anton Siedlecki
 * Date:     2026-08-12
 * Version:  1.0.0
 * Purpose:  Enhances the "Load more" fallback link rendered by
 *           sections/main-collection-product-grid.liquid (only when
 *           that section's `pagination_type` setting is `infinite`)
 *           into auto-loading infinite scroll. Fetches the next page,
 *           appends its product cards to the current grid, and moves
 *           the trigger's `data-next-url` forward to whatever the
 *           freshly-fetched page's own trigger points to next.
 *
 *           Modeled directly on the existing, proven fetch/parse
 *           pattern in assets/component-facets.js
 *           (FacetFiltersForm.renderProductGridContainer) — that method
 *           REPLACES #ProductGridContainer for a filter change; this
 *           APPENDS new <li> items to the existing #product-grid <ul>
 *           instead, since infinite scroll accumulates rather than
 *           resets the grid.
 *
 *           Graceful degradation: the trigger is a real <a href> to the
 *           next page. If this script fails to load/run, or a shopper
 *           middle-clicks/opens it in a new tab, or presses Enter while
 *           it's focused before it scrolls into view, it's a normal,
 *           working pagination link — never a dead button.
 *
 *           After appending, dispatches 'native:grid:updated' (bubbles)
 *           — the same event assets/component-facets.js already fires
 *           after a filter re-render — so native-collection-swatches.js
 *           re-initializes swatches on the newly-added cards without any
 *           changes needed to that file.
 *
 *           Also LISTENS for that same 'native:grid:updated' event to
 *           re-bind any freshly-swapped-in trigger: when a shopper
 *           changes a filter/sort while this section also has infinite
 *           scroll enabled, component-facets.js replaces the entire
 *           #ProductGridContainer (including this trigger) with the
 *           filtered set's own first page. Without re-binding, that new
 *           trigger would have no click/observer listeners at all.
 *           data-infinite-scroll-bound marks a trigger already wired up,
 *           mirroring native-collection-swatches.js's data-ncs-ready
 *           guard against re-binding the same element twice.
 */

(function () {
  'use strict';

  function bindTriggers() {
    document.querySelectorAll('[data-infinite-scroll-trigger]:not([data-infinite-scroll-bound])').forEach(function (trigger) {
      trigger.setAttribute('data-infinite-scroll-bound', '1');
      bindTrigger(trigger);
    });
  }

  function bindTrigger(trigger) {
    var grid = document.getElementById('product-grid');
    if (!grid) return;

    var link = trigger.querySelector('[data-infinite-scroll-link]');
    var spinner = trigger.querySelector('.loading__spinner');
    var loading = false;
    var observer = null;

    function setLoading(isLoading) {
      loading = isLoading;
      trigger.classList.toggle('is-loading', isLoading);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
      if (link) link.setAttribute('aria-hidden', isLoading ? 'true' : 'false');
    }

    function stop() {
      if (observer) observer.disconnect();
      trigger.remove();
    }

    function loadNext() {
      var nextUrl = trigger.dataset.nextUrl;
      if (!nextUrl || loading) return;

      setLoading(true);

      // Fetch just this section's rendered HTML (same lightweight request
      // assets/component-facets.js uses for its AJAX filter re-render)
      // instead of the full page.
      var sectionId = trigger.dataset.sectionId;
      var fetchUrl = sectionId
        ? nextUrl + (nextUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId
        : nextUrl;

      fetch(fetchUrl)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.getElementById('product-grid');
          var newTrigger = doc.querySelector('[data-infinite-scroll-trigger]');

          if (newGrid) {
            Array.from(newGrid.children).forEach(function (item) {
              grid.appendChild(document.importNode(item, true));
            });
          }

          // Deliberately NOT updating the URL/history here — the address bar
          // stays on the original collection URL (no ?page=N) for the whole
          // infinite-scroll session, per explicit request. The real <a href>
          // fallback link still navigates normally with ?page=N when JS is
          // off; that's a full page load, not this in-page fetch path.
          //
          // detail.reason: 'scroll' (2026-08-25) — lets listeners (namely
          // assets/native-collection-size-filter.js) tell an infinite-scroll
          // page append apart from a real filter change (which dispatches
          // this same event name from assets/component-facets.js with
          // reason: 'filter'). An append just needs existing size-filter
          // state re-applied to the newly-added cards, not a full refetch.
          document.dispatchEvent(new CustomEvent('native:grid:updated', { bubbles: true, detail: { reason: 'scroll' } }));
          if (typeof initializeSwipeFunctionality === 'function') {
            initializeSwipeFunctionality(document.getElementById('ProductGridContainer'));
          }

          var newNextUrl = newTrigger && newTrigger.dataset.nextUrl;
          if (newNextUrl) {
            trigger.dataset.nextUrl = newNextUrl;
            if (link) link.href = newNextUrl;
          } else {
            // Fetched page had no further "next" trigger — that was the last page.
            stop();
          }
        })
        .catch(function () {
          // Fail open: leave the real <a href> in place so the shopper can
          // still click through to the next page manually.
        })
        .finally(function () {
          setLoading(false);
        });
    }

    if (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        loadNext();
      });
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadNext();
          });
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(trigger);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTriggers);
  } else {
    bindTriggers();
  }

  document.addEventListener('native:grid:updated', bindTriggers);
})();
